import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import API from "../services/api";
import Loader from "../components/Loader";
import { FaSearch, FaHistory, FaFilter } from "react-icons/fa";

function AuditLogs() {
    const [logs, setLogs] = useState([]);
    const [search, setSearch] = useState("");
    const [moduleFilter, setModuleFilter] = useState("");
    const [actionFilter, setActionFilter] = useState("");
    const [loading, setLoading] = useState(true);
    
    // Pagination state
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);
    const limit = 10;

    // =====================================
    // FETCH AUDIT LOGS
    // =====================================
    const fetchLogs = async (currentPage = 1) => {
        try {
            setLoading(true);
            let url = `/log/list?page=${currentPage}&limit=${limit}`;
            
            if (moduleFilter) url += `&module=${moduleFilter}`;
            if (actionFilter) url += `&action=${actionFilter}`;
            if (search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;

            const response = await API.get(url);
            setLogs(response.data.logs);
            setTotalLogs(response.data.totalLogs);
            setTotalPages(response.data.totalPages || 1);
        } catch (error) {
            console.error("Failed to fetch logs", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, moduleFilter, actionFilter]);

    // Trigger search on submit or enter
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setPage(1);
        fetchLogs(1);
    };

    // Helper to color-code actions
    const getActionBadgeStyle = (action) => {
        switch (action) {
            case "CREATE":
                return { background: "rgba(16, 185, 129, 0.1)", color: "#10b981" };
            case "UPDATE":
                return { background: "rgba(245, 158, 11, 0.1)", color: "#f59e0b" };
            case "DELETE":
                return { background: "rgba(239, 68, 68, 0.1)", color: "#ef4444" };
            case "LOGIN":
                return { background: "rgba(79, 70, 229, 0.1)", color: "#4f46e5" };
            case "LOGOUT":
                return { background: "rgba(100, 116, 139, 0.1)", color: "#64748b" };
            case "EXPORT":
                return { background: "rgba(14, 165, 233, 0.1)", color: "#0ea5e9" };
            default:
                return { background: "rgba(203, 213, 225, 0.1)", color: "#475569" };
        }
    };

    return (
        <div className="main-wrapper">
            <Sidebar />

            <div className="content-wrapper">
                {/* HEADER */}
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 mb-4 animate-fade-in">
                    <div>
                        <h2 className="fw-extrabold text-slate mb-1">System Audit Logs</h2>
                        <p className="text-muted mb-0">Monitor all data creations, updates, deletions, and logins</p>
                    </div>
                </div>

                {/* FILTERS & SEARCH */}
                <div className="card border-0 shadow-sm mb-4 animate-fade-in" style={{ animationDelay: "0.1s" }}>
                    <div className="card-body p-3">
                        <form onSubmit={handleSearchSubmit} className="row g-3">
                            {/* Search Bar */}
                            <div className="col-12 col-md-4">
                                <div className="input-group">
                                    <span className="input-group-text bg-transparent border-0 pe-0" style={{ color: "#94a3b8" }}>
                                        <FaSearch />
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Search logs (Details, Performed By, IP)..."
                                        className="form-control border-0 shadow-none bg-transparent"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Module Filter */}
                            <div className="col-6 col-md-3">
                                <div className="input-group">
                                    <span className="input-group-text bg-transparent border-0 pe-0" style={{ color: "#94a3b8" }}>
                                        <FaFilter size={12} />
                                    </span>
                                    <select
                                        className="form-select border-0 shadow-none bg-transparent"
                                        value={moduleFilter}
                                        onChange={(e) => {
                                            setModuleFilter(e.target.value);
                                            setPage(1);
                                        }}
                                    >
                                        <option value="">All Modules</option>
                                        <option value="USER">User Auth</option>
                                        <option value="PROFILE">Profiles</option>
                                        <option value="DEPARTMENT">Departments</option>
                                    </select>
                                </div>
                            </div>

                            {/* Action Filter */}
                            <div className="col-6 col-md-3">
                                <div className="input-group">
                                    <span className="input-group-text bg-transparent border-0 pe-0" style={{ color: "#94a3b8" }}>
                                        <FaFilter size={12} />
                                    </span>
                                    <select
                                        className="form-select border-0 shadow-none bg-transparent"
                                        value={actionFilter}
                                        onChange={(e) => {
                                            setActionFilter(e.target.value);
                                            setPage(1);
                                        }}
                                    >
                                        <option value="">All Actions</option>
                                        <option value="CREATE">CREATE</option>
                                        <option value="UPDATE">UPDATE</option>
                                        <option value="DELETE">DELETE</option>
                                        <option value="LOGIN">LOGIN</option>
                                        <option value="LOGOUT">LOGOUT</option>
                                        <option value="EXPORT">EXPORT</option>
                                    </select>
                                </div>
                            </div>

                            {/* Search Submit Button */}
                            <div className="col-12 col-md-2 d-flex align-items-center">
                                <button type="submit" className="btn btn-dark w-100 py-2">
                                    Search
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {loading ? (
                    <Loader message="Loading activity logs..." />
                ) : (
                    <>
                        {/* TABLE CARD */}
                        <div className="card border-0 shadow-sm animate-fade-in" style={{ animationDelay: "0.2s" }}>
                            <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-4">Timestamp</th>
                                        <th>Performed By</th>
                                        <th>Action</th>
                                        <th>Module</th>
                                        <th>Details</th>
                                        <th className="pe-4">IP Address</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.length > 0 ? (
                                        logs.map((log) => (
                                            <tr key={log._id} className="hover-bg-light">
                                                <td className="ps-4 text-muted font-monospace small">
                                                    {new Date(log.createdAt).toLocaleString()}
                                                </td>
                                                <td>
                                                    <div className="fw-bold text-dark">{log.performedByName}</div>
                                                    <div className="text-muted small">{log.performedByEmail || "System Account"}</div>
                                                </td>
                                                <td>
                                                    <span 
                                                        className="badge px-3 py-2 text-uppercase font-monospace"
                                                        style={{
                                                            ...getActionBadgeStyle(log.action),
                                                            fontWeight: "700"
                                                        }}
                                                    >
                                                        {log.action}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="fw-semibold text-slate small">
                                                        {log.module}
                                                    </span>
                                                </td>
                                                <td style={{ maxWidth: "350px", wordBreak: "break-word" }}>
                                                    <span className="text-dark small">{log.details}</span>
                                                </td>
                                                <td className="pe-4 font-monospace text-muted small">
                                                    {log.ipAddress}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center py-5 text-muted">
                                                <FaHistory className="text-muted mb-3" size={32} />
                                                <h5>No Activity Logs Found</h5>
                                                <p className="small mb-0">Try adjusting your search query or filters.</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* PAGINATION */}
                {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4 animate-fade-in">
                        <span className="text-muted small">
                            Showing {logs.length} of {totalLogs} log entries
                        </span>
                        <div className="d-flex align-items-center gap-2">
                            {[...Array(totalPages)].map((_, index) => (
                                <button
                                    key={index}
                                    className={`btn d-flex align-items-center justify-content-center font-semibold`}
                                    style={{
                                        width: "40px",
                                        height: "40px",
                                        padding: "0",
                                        borderRadius: "50%",
                                        backgroundColor: page === index + 1 ? "var(--primary-color)" : "#ffffff",
                                        color: page === index + 1 ? "#ffffff" : "var(--text-muted)",
                                        border: page === index + 1 ? "none" : "1px solid var(--border-light)",
                                        boxShadow: page === index + 1 ? "0 4px 10px rgba(79, 70, 229, 0.2)" : "none"
                                    }}
                                    onClick={() => setPage(index + 1)}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                    </>
                )}
            </div>
        </div>
    );
}

export default AuditLogs;
